<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <div>
    <div
      class="mb-3 flex items-center gap-2"
      data-test="graphics-editor-timer-status"
    >
      <span class="inline-block h-2.5 w-2.5 rounded-full" :class="statusDotClass" />
      <span class="text-[13px]">
        Status: <strong>{{ timerStatusLabel }}</strong> - <span data-test="graphics-editor-timer-value">{{ timerValue }}</span>
      </span>
    </div>

    <div class="action-key-container">
      <div class="hot-keys-desc my-2">
        Trigger hotkey by pressing buttons or use keyboard shortcuts as shown.
      </div>

      <div class="action-keys">
        <actionKey
          v-for="(action, index) in actions"
          :key="index"
          :action="action"
          :disabled="isActionDisabled(action.value)"
          :production_context="production_context"
          :data-test="`graphics-editor-timer-${action.hotkey.toLowerCase()}`"
        />
      </div>
    </div>

    <label v-if="counterFormat == 'Time'">Duration</label>

    <div class="timer-preview-group" v-if="counterFormat == 'Time'">
      <div class="timer-preview-select">
        <div class="timer-select">
          <select
            class="tb-select"
            v-model="hourPreview"
            data-entity="producer-editor-selectHourPreview"
            data-test="graphics-editor-timer-hourDropdown"
          >
            <option
              v-for="hour in hoursList"
              :key="hour"
            >
              {{ hour }}
            </option>
          </select>
        </div>

        <label class="sidebar-input-sub-label">H</label>
      </div>

      <div class="timer-preview-select">
        <div class="timer-select">
          <select
            class="tb-select"
            v-model="minutePreview"
            data-entity="producer-editor-selectMinutePreview"
            data-test="graphics-editor-timer-minuteDropdown"
          >
            <option
              v-for="minute in minutesList"
              :key="minute"
            >
              {{ minute }}
            </option>
          </select>
        </div>

        <label class="sidebar-input-sub-label">M</label>
      </div>

      <div class="timer-preview-select">
        <div class="timer-select">
          <select
            class="tb-select"
            v-model="secondPreview"
            data-entity="producer-editor-selectSecondPreview"
            data-test="graphics-editor-timer-secondDropdown"
          >
            <option
              v-for="second in secondsList"
              :key="second"
            >
              {{ second }}
            </option>
          </select>
        </div>

        <label class="sidebar-input-sub-label">S</label>
      </div>
    </div>

    <div
      v-if="counterFormat == 'Date & Time'"
      class="datepicker"
      :class="{ 'picker-flip-up': flipPickerUp }"
    >
      <label
        class="tb-label"
        data-test="graphics-editor-timer-targetDate"
      >
        Countdown to
      </label>

      <date-picker
        v-model="dateTimeModel"
        minimum-view="time"
        input-format="MM/dd/yyyy hh:mm aa"
        :clearable="false"
        :starting-view-date="safeDefaultDate"
        :disabled-dates="disabledDatesOptions"
        :disabled-time="disabledTimeOptions"
        placeholder="Select date and time"
        data-entity="producer-editor-dateTimePicker"
        data-test="graphics-editor-timer-picker"
        @opened="handlePickerOpened"
        @closed="handlePickerClosed"
      />
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import DatePicker from 'vue3-datepicker';

import ActionKey from '../../../../panel-editor/hotkeys/ActionKey.vue';

const STORED_DATE_TIME_FORMAT = 'MM/DD/YYYY hh:mm A';

dayjs.extend(customParseFormat);

export default {
  props: ['element', 'graphic_fields', 'production_context'],

  components: {
    DatePicker,
    ActionKey,
  },

  data() {
    return {
      hoursList: [],
      minutesList: [],
      secondsList: [],
      hourCutoffPreview: 0,
      minuteCutoffPreview: 0,
      secondCutoffPreview: 0,
      // Flip the picker popup above the input when it would otherwise extend
      // past the bottom of the settings panel and appear off screen.
      flipPickerUp: false,
      // Timestamp used to render the current timer value; refreshed on
      // whole-second boundaries so the readout stays current even while the
      // graphic is not live.
      now: Date.now(),
      nowTimeout: null,
      actions: [
        {
          hotkey: 'S',
          display: 'Start',
          description: 'Use this hotkey to start the timer',
          value: 'start',
          type: 'timer widget',
          elementId: this.element.id,
        },
        {
          hotkey: 'P',
          display: 'Pause',
          description: 'Use this hotkey to pause the timer',
          value: 'pause',
          type: 'timer widget',
          elementId: this.element.id,
        },
        {
          hotkey: 'R',
          display: 'Reset',
          description: 'Use this hotkey to reset the timer',
          value: 'reset',
          type: 'timer widget',
          elementId: this.element.id,
        },
      ],
    };
  },

  computed: {
    elementFields() {
      return this.graphic_fields?.[this.element.id] || {};
    },

    counterFormat() {
      return this.getFieldValue('counterFormat');
    },

    /**
     * True for countdowns that target a specific date & time. These auto-run
     * whenever they're live, so their status defaults to running (instead of
     * stopped) unless they've been explicitly paused.
     */
    isAutoRunCountdown() {
      return this.counterFormat === 'Date & Time'
        && this.timerType === 'Countdown';
    },

    timerType() {
      return this.getFieldValue('timerType');
    },

    /**
     * Current timer state, derived from the fields Producer tracks.
     * - running: playTimer is set
     * - paused: playTimer is off but an elapsed duration (Time mode)
     *   or an explicit paused marker (Date & Time countdowns) exists
     * - stopped: reset or never started
     */
    timerStatus() {
      // Date & Time countdowns auto-run whenever they're live, so they default
      // to running unless explicitly paused.
      if (this.isAutoRunCountdown) {
        return this.getFieldValue('paused') ? 'paused' : 'running';
      }

      if (this.getFieldValue('playTimer')) {
        return 'running';
      }

      const duration = this.getFieldValue('duration');

      if (duration != null || this.getFieldValue('paused')) {
        return 'paused';
      }

      return 'stopped';
    },

    /**
     * Current value of the timer, derived from the same fields the live timer
     * uses, so the settings show what the timer is at even when the graphic is
     * not live or visible. Ticks every second via `now`.
     */
    timerValue() {
      const { now, timerType, counterFormat } = this;

      if (counterFormat === 'Date & Time') {
        const dateTime = this.getFieldValue('dateTime');
        const target = dateTime ? dayjs(dateTime) : null;

        // A paused Date & Time timer shows the value frozen at the pause
        // instant (stored when the pause action ran) instead of the live
        // countdown/count-up to the target date, which would keep counting
        // while paused.
        if (this.getFieldValue('paused')) {
          const pausedRemaining = this.getFieldValue('pausedRemaining');

          if (pausedRemaining != null) {
            return this.formatTimerValue(Math.max(pausedRemaining, 0));
          }
        }

        if (target && target.isValid()) {
          if (timerType === 'Countdown') {
            return this.formatTimerValue(Math.max(target.diff(now) / 1000, 0));
          }

          return this.formatTimerValue(Math.max(now - target.valueOf(), 0) / 1000);
        }

        return this.formatTimerValue(0);
      }

      const startMoment = this.getFieldValue('startMoment');
      const startMs = startMoment && dayjs(startMoment).isValid() ? dayjs(startMoment).valueOf() : 0;
      const runningElapsedMs = startMs ? Math.max(now - startMs, 0) : 0;

      if (timerType === 'Stopwatch') {
        // Stopwatch duration is tracked in milliseconds
        const elapsedMs = (this.getFieldValue('duration') ?? 0) + runningElapsedMs;
        return this.formatTimerValue(elapsedMs / 1000);
      }

      // Countdown / Count Up duration is tracked in seconds
      const elapsedSec = (this.getFieldValue('duration') ?? 0) + (runningElapsedMs / 1000);

      if (timerType === 'Countdown') {
        return this.formatTimerValue(Math.max((this.initialDurationMs() / 1000) - elapsedSec, 0));
      }

      return this.formatTimerValue(elapsedSec);
    },

    timerStatusLabel() {
      const labels = {
        running: 'Running',
        paused: 'Paused',
        stopped: 'Stopped',
      };

      return labels[this.timerStatus];
    },

    statusDotClass() {
      const classes = {
        running: 'bg-[#00d483]',
        paused: 'bg-[#ffb020]',
        stopped: 'bg-[#7b7b7b]',
      };

      return classes[this.timerStatus];
    },

    playBtn() {
      const { play, timerType } = this.element;

      if (play) {
        return timerType === 'Stopwatch' ? 'Stop' : 'Pause';
      }

      return timerType === 'Stopwatch' ? 'Start' : 'Play';
    },

    dateTimeModel: {
      get() {
        const dateTime = this.getFieldValue('dateTime');

        if (!dateTime) {
          return null;
        }

        const parsed = typeof dateTime === 'string'
          ? dayjs(dateTime, STORED_DATE_TIME_FORMAT, true)
          : dayjs(dateTime);
        return parsed.isValid() ? parsed.toDate() : null;
      },
      set(value) {
        this.setDateTime(value);
      },
    },

    hourPreview: {
      get() {
        return this.getFieldValue('hourPreview') ?? 0;
      },
      set(value) {
        this.updateField('hourPreview', parseInt(value, 10));
      },
    },

    minutePreview: {
      get() {
        return this.getFieldValue('minutePreview') ?? 0;
      },
      set(value) {
        this.updateField('minutePreview', parseInt(value, 10));
      },
    },

    secondPreview: {
      get() {
        return this.getFieldValue('secondPreview') ?? 0;
      },
      set(value) {
        this.updateField('secondPreview', parseInt(value, 10));
      },
    },

    safeDefaultDate() {
      const dateTime = this.getFieldValue('dateTime');
      const parsed = typeof dateTime === 'string'
        ? dayjs(dateTime, STORED_DATE_TIME_FORMAT, true)
        : dayjs(dateTime);

      return parsed.isValid() ? parsed.toDate() : new Date();
    },

    disabledDatesOptions() {
      return {
        predicate: (date) => this.isDisabledDate(date),
      };
    },

    disabledTimeOptions() {
      return {
        predicate: (date) => this.isDisabledTime(date),
      };
    },
  },

  mounted() {
    // create values for select menus
    for (let i = 0; i < 60; i += 1) {
      if (i < 24) this.hoursList.push(i);
      this.minutesList.push(i);
      this.secondsList.push(i);
    }

    // reset element id if we select a new layer while the layer settings panel is already open
    // vue has poor reactivity support for values in arrays
    this.$watch(() => this.element, () => {
      this.actions.forEach((action) => {
        action.elementId = this.element.id; // eslint-disable-line no-param-reassign
      });
    });

    // Keep the timer value readout current while the settings are open,
    // ticking on whole-second boundaries so it flips at the same moment as the
    // live and preview outputs.
    this.scheduleNowTick();
  },

  beforeUnmount() {
    clearTimeout(this.nowTimeout);
  },

  methods: {
    updateField(field, value) {
      return this.$emit('value', {
        value,
        field,
      });
    },

    getFieldValue(field) {
      // Read the field directly (instead of a hasOwnProperty guard) so the
      // reactive dependency on the key is registered even when the field
      // doesn't exist yet. Otherwise, the first time a field like `playTimer`
      // is added to the element's fields, the status wouldn't update until the
      // settings panel remounts.
      const fieldValue = this.elementFields?.[field];

      if (fieldValue !== undefined) {
        return fieldValue;
      }

      // Runtime state fields are deleted from the fields when they no longer
      // apply (e.g. `startMoment` is removed on pause/reset). The `element`
      // snapshot is merged from the fields at selection time, so it can still
      // carry a stale copy of these - falling back to it would make the
      // readout keep counting after a pause or ignore a reset. Only fall back
      // to the element for config/display fields that legitimately live on the
      // template element and are never deleted.
      const runtimeStateFields = ['playTimer', 'startMoment', 'duration', 'paused', 'pausedRemaining'];

      if (!runtimeStateFields.includes(field)
        && Object.prototype.hasOwnProperty.call(this.element || {}, field)) {
        return this.element[field];
      }

      return null;
    },

    /**
     * Starting duration in ms for Time-based timers, from the preview fields.
     */
    initialDurationMs() {
      const d = this.getFieldValue('dayPreview') ?? 0;
      const h = this.getFieldValue('hourPreview') ?? 0;
      const m = this.getFieldValue('minutePreview') ?? 0;
      const s = this.getFieldValue('secondPreview') ?? 0;

      return ((d * 86400) + (h * 3600) + (m * 60) + s) * 1000;
    },

    /**
     * Format a total number of seconds using the timer's display settings
     * (which units are shown and whether dividers are shown).
     */
    formatTimerValue(totalSeconds) {
      const showDays = this.getFieldValue('showDays') ?? false;
      const showHours = this.getFieldValue('showHours') ?? true;
      const showMinutes = this.getFieldValue('showMinutes') ?? true;
      const showSeconds = this.getFieldValue('showSeconds') ?? true;
      const showDividers = this.getFieldValue('showDividers') !== false;

      const pad = (n) => String(n).padStart(2, '0');
      const wholeSeconds = Math.floor(totalSeconds);
      const days = Math.floor(wholeSeconds / 86400);

      // When a higher unit is hidden, the next shown unit absorbs the hidden
      // time and shows the total (e.g. hours hidden -> minutes show 90 for
      // 1h30m). Once any higher unit is shown, lower units stay in their own
      // range, mirroring the live/preview renderers.
      const hours = showDays
        ? Math.floor(wholeSeconds / 3600) % 24
        : Math.floor(wholeSeconds / 3600);
      const minutes = (showDays || showHours)
        ? Math.floor(wholeSeconds / 60) % 60
        : Math.floor(wholeSeconds / 60);
      const seconds = (showDays || showHours || showMinutes)
        ? wholeSeconds % 60
        : wholeSeconds;

      const parts = [];
      if (showDays) parts.push(pad(days));
      if (showHours) parts.push(pad(hours));
      if (showMinutes) parts.push(pad(minutes));
      if (showSeconds) parts.push(pad(seconds));

      return parts.join(showDividers ? ':' : ' ');
    },

    /**
     * Schedule the next `now` update on a whole-second boundary so the readout
     * is derived from the same wall-clock timestamps as the live and preview
     * renderers (which also tick on second boundaries).
     */
    scheduleNowTick() {
      const delay = 1000 - (Date.now() % 1000);

      this.nowTimeout = setTimeout(() => {
        // Keep `now` frozen while a Date & Time timer is paused so paused
        // timers that don't carry a stored pausedRemaining (legacy data) also
        // show a stable value instead of counting down/up.
        const isPausedDateTime = this.counterFormat === 'Date & Time'
          && this.getFieldValue('paused');

        if (!isPausedDateTime) {
          this.now = Date.now();
        }

        this.scheduleNowTick();
      }, delay);
    },

    /**
     * Disables the action key that doesn't apply to the timer's current state
     * (Start while running, Pause while not running). Reset stays enabled.
     */
    isActionDisabled(value) {
      if (value === 'start') {
        return this.timerStatus === 'running';
      }

      if (value === 'pause') {
        return this.timerStatus !== 'running';
      }

      return false;
    },

    /**
     * Keeps the picker popup on screen: if the popup would extend past the
     * bottom of the scrollable settings panel (and there's room above the
     * input), flip it to open upward instead.
     */
    handlePickerOpened() {
      this.$nextTick(() => {
        const popup = this.$el?.querySelector('.v3dp__popout');
        const panel = this.$el?.closest('.graphic-edit-fields');

        if (!popup || !panel) {
          return;
        }

        const popupRect = popup.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        const inputRect = this.$el
          ?.querySelector('.v3dp__input_wrapper input')
          ?.getBoundingClientRect();

        const overflowsBottom = popupRect.bottom > panelRect.bottom;
        const hasRoomAbove = !!inputRect
          && (inputRect.top - panelRect.top) >= popupRect.height;

        this.flipPickerUp = overflowsBottom && hasRoomAbove;
      });
    },

    handlePickerClosed() {
      this.flipPickerUp = false;
    },

    setDateTime(value) {
      const formattedValue = value ? dayjs(value).format(STORED_DATE_TIME_FORMAT) : null;

      return this.updateField('dateTime', formattedValue);
    },

    /**
    * Returns true if date should be disabled
    */
    isDisabledDate(date) {
      const targetDate = dayjs(date);
      if (!targetDate.isValid()) {
        return false;
      }

      const todayStart = dayjs().startOf('day');

      if (this.timerType === 'Countdown') {
        return targetDate.isBefore(todayStart);
      }

      return targetDate.isAfter(todayStart);
    },

    isDisabledTime(date) {
      const targetDate = dayjs(date);
      if (!targetDate.isValid()) {
        return false;
      }

      if (this.timerType === 'Countdown') {
        return targetDate.isBefore();
      }

      return targetDate.isAfter();
    },
  },
};
</script>
